import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { 
  Github, Linkedin, Twitter, Link as LinkIcon, Briefcase, Code, User, Mail, Activity
} from 'lucide-react';
import LiveCodingStats from '../portfolio/LiveCodingStats';

function TiltCard({ children, className }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [15, -15]);
  const rotateY = useTransform(x, [-100, 100], [-15, 15]);

  function handleMouse(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    x.set(event.clientX - rect.left - rect.width / 2);
    y.set(event.clientY - rect.top - rect.height / 2);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      style={{ perspective: 1000 }}
      onMouseMove={handleMouse}
      onMouseLeave={handleMouseLeave}
      className={className}
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="w-full h-full"
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

export default function Creative3DTheme({ profileData, socials, activeColor, trackSocialClick }) {
  const { core, experience, projects, skills } = profileData;

  let bgGradient = 'from-slate-950 to-slate-900';
  if (activeColor.bg.includes('blue')) {
    bgGradient = 'from-blue-950 to-indigo-950';
  } else if (activeColor.bg.includes('emerald')) {
    bgGradient = 'from-emerald-950 to-teal-950';
  } else if (activeColor.bg.includes('purple')) {
    bgGradient = 'from-purple-950 to-fuchsia-950';
  } else if (activeColor.bg.includes('rose')) {
    bgGradient = 'from-rose-950 to-red-950';
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${bgGradient} text-white font-sans overflow-x-hidden selection:bg-white/20 pb-24`}>
      <Helmet><title>{core.name} | Creative 3D</title></Helmet>

      {/* Floating Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 100, repeat: Infinity, ease: "linear" }} className="absolute -top-32 -left-32 w-96 h-96 border border-white/5 rounded-3xl" />
        <motion.div animate={{ rotate: -360 }} transition={{ duration: 150, repeat: Infinity, ease: "linear" }} className="absolute top-1/2 right-10 w-[500px] h-[500px] border border-white/5 rounded-full" />
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-24 relative z-10 space-y-32">
        
        {/* Hero Section */}
        <section className="min-h-[60vh] flex flex-col items-center justify-center text-center space-y-8">
          <TiltCard className="w-full max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-12 sm:p-20 rounded-[3rem] shadow-2xl relative" style={{ transform: "translateZ(50px)" }}>
              <div className="absolute -top-6 -left-6 w-12 h-12 bg-white/20 rounded-full blur-md" />
              <div className="absolute -bottom-8 -right-8 w-20 h-20 bg-white/10 rounded-full blur-xl" />
              
              <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} style={{ transform: "translateZ(80px)" }}>
                <h1 className="text-5xl sm:text-7xl font-black tracking-tight mb-6">{core.name}</h1>
                <p className="text-xl sm:text-2xl text-white/80 font-light max-w-2xl mx-auto leading-relaxed">
                  {core.summary || 'A creative professional building innovative solutions.'}
                </p>
              </motion.div>

              {/* Social Links */}
              <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="flex justify-center gap-6 mt-12" style={{ transform: "translateZ(60px)" }}>
                {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" onClick={() => trackSocialClick && trackSocialClick('GitHub')} className="p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all hover:scale-110"><Github className="w-6 h-6" /></a>}
                {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" onClick={() => trackSocialClick && trackSocialClick('LinkedIn')} className="p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all hover:scale-110"><Linkedin className="w-6 h-6" /></a>}
                {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" onClick={() => trackSocialClick && trackSocialClick('Twitter')} className="p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all hover:scale-110"><Twitter className="w-6 h-6" /></a>}
                {socials.custom && <a href={socials.custom} target="_blank" rel="noreferrer" onClick={() => trackSocialClick && trackSocialClick('Custom Link')} className="p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all hover:scale-110"><LinkIcon className="w-6 h-6" /></a>}
              </motion.div>
            </div>
          </TiltCard>
        </section>

        {/* Experience Grid */}
        {experience.length > 0 && (
          <section className="space-y-12">
            <div className="flex items-center gap-4 justify-center text-3xl font-bold">
              <Briefcase className="w-8 h-8" /> Experience
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {experience.map((exp, idx) => (
                <TiltCard key={idx} className="h-full">
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl h-full shadow-lg" style={{ transform: "translateZ(30px)" }}>
                    <div className="text-white/50 text-sm font-bold uppercase tracking-wider mb-2" style={{ transform: "translateZ(40px)" }}>
                      {exp.start_date} - {exp.end_date || 'Present'}
                    </div>
                    <h3 className="text-2xl font-bold mb-1" style={{ transform: "translateZ(50px)" }}>{exp.job_title}</h3>
                    <div className="text-lg text-white/80 mb-6" style={{ transform: "translateZ(40px)" }}>{exp.company}</div>
                    <p className="text-white/60 leading-relaxed" style={{ transform: "translateZ(30px)" }}>{exp.description}</p>
                  </div>
                </TiltCard>
              ))}
            </div>
          </section>
        )}

        {/* Projects Grid */}
        {projects.length > 0 && (
          <section className="space-y-12">
            <div className="flex items-center gap-4 justify-center text-3xl font-bold">
              <Code className="w-8 h-8" /> Projects
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((proj, idx) => (
                <TiltCard key={idx} className="h-full">
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl h-full shadow-lg flex flex-col justify-between group" style={{ transform: "translateZ(30px)" }}>
                    <div>
                      <h3 className="text-xl font-bold mb-4" style={{ transform: "translateZ(50px)" }}>{proj.title}</h3>
                      <p className="text-white/60 text-sm leading-relaxed mb-6" style={{ transform: "translateZ(30px)" }}>{proj.description}</p>
                    </div>
                    {proj.link && (
                      <a href={proj.link} target="_blank" rel="noreferrer" className="inline-block py-2 px-4 bg-white/10 rounded-xl text-sm font-bold hover:bg-white/20 transition-colors text-center" style={{ transform: "translateZ(40px)" }}>
                        View Project
                      </a>
                    )}
                  </div>
                </TiltCard>
              ))}
            </div>
          </section>
        )}

        {/* Live Coding Stats */}
        {(socials.github || socials.leetcode) && (
          <section className="space-y-12">
            <div className="flex items-center gap-4 justify-center text-3xl font-bold">
              <Activity className="w-8 h-8" /> Live Stats
            </div>
            <TiltCard className="max-w-4xl mx-auto w-full">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl shadow-lg" style={{ transform: "translateZ(30px)" }}>
                 <LiveCodingStats githubUrl={socials.github} leetcodeUrl={socials.leetcode} activeColor={activeColor} themeType="dark" />
              </div>
            </TiltCard>
          </section>
        )}

        {/* Skills */}
        {skills.length > 0 && (
          <section className="space-y-12 text-center">
            <div className="flex items-center gap-4 justify-center text-3xl font-bold">
              <User className="w-8 h-8" /> Expertise
            </div>
            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
              {skills.map((sk, idx) => (
                <TiltCard key={idx}>
                  <div className="px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-sm font-bold shadow-xl" style={{ transform: "translateZ(20px)" }}>
                    {sk.name || sk}
                  </div>
                </TiltCard>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
